<!--
 - Manage lookaheads component (manage-lookaheads.component.html).
 -
 - @author Ryan Sweeney <rsweeney@buildcentrix.com>
 - @copyright (c) 2022-2024 Build Centrix
 -->
<div class="manage-lookaheads">
  <bc-templates-modal-layout
    [isLoading]="isLoading"
    [loadingText]="('actions.loading'|translate)"
    (onCloseButtonClicked)="onDismiss()"
  >
    <!--Dialog Title-->
    <div title-content>
      {{'actions.addType'|translate: { type:
      ('order.types.lookahead.description'|translate) } }}
    </div>
    <!--Dialog Content-->
    <div body-content>
      <form #lookaheadsForm="ngForm">
        <div class="row">
          <div class="col-xs-12 col-sm-4">
            <!--Label / Tag-->
            <bc-atoms-input-group
              labelIcon="workOrders"
              labelTitle="{{'order.lookahead.label.title'|translate}}"
              labelDescription="{{'order.lookahead.label.description'|translate}}"
              [required]="true"
              [state]="labelTagControl.status"
              [stateInput]="labelTagControl.errors"
              [stateTrigger]="labelTagControl.invalid && (labelTagControl.touched && labelTagControl.dirty)"
            >
              <bc-atoms-input
                id="tag"
                name="tag"
                title="{{'order.lookahead.label.description'|translate}}"
                placeholder="{{'order.lookahead.label.title'|translate}}"
                size="small"
                [required]="true"
                [disabled]="isSaving"
                [(model)]="lookahead.label"
                [(ngModel)]="lookahead.label"
                #labelTagControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-input>
            </bc-atoms-input-group>
          </div>
          <div class="col-xs-12 col-sm-8">
            <!--Description / Special Instructions-->
            <bc-atoms-input-group
              labelIcon="notifications"
              labelTitle="{{'order.lookahead.description.title'|translate}}"
              labelDescription="{{'order.lookahead.description.description'|translate}}"
              [required]="false"
              [state]="descriptionInstructionsControl.status"
              [stateInput]="descriptionInstructionsControl.errors"
              [stateTrigger]="descriptionInstructionsControl.invalid && (descriptionInstructionsControl.touched && descriptionInstructionsControl.dirty)"
            >
              <bc-atoms-textarea
                id="descriptionInstructions"
                name="descriptionInstructions"
                placeholder="{{'order.lookahead.description.title'|translate}}"
                title="{{'order.lookahead.description.description'|translate}}"
                size="small"
                [required]="false"
                [disabled]="isSaving"
                [rows]="1"
                [(model)]="lookahead.description"
                [(ngModel)]="lookahead.description"
                #descriptionInstructionsControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-textarea>
            </bc-atoms-input-group>
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12">
            <hr />
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12 col-sm-4">
            <!--Job-->
            <bc-atoms-input-group
              labelIcon="jobsAdmin"
              labelTitle="{{'order.job.title'|translate}}"
              labelDescription="{{'order.job.description'|translate}}"
              [required]="true"
              [state]="jobControl.status"
              [stateInput]="jobControl.errors"
              [stateTrigger]="jobControl.invalid && (jobControl.touched && jobControl.dirty)"
              [loading]="wdJobService.searchingJobs"
              [loadingText]="('labels.loadingResults'|translate)"
            >
              <!--Search Typeahead-->
              <label for="jobCombined" [hidden]="true"></label>
              <input
                id="jobCombined"
                name="jobCombined"
                title="{{'order.job.description'|translate}}"
                placeholder="{{'order.job.title'|translate}}"
                type="text"
                class="form-control form-control-sm"
                [class.is-invalid]="wdJobService.searchingJobsFailed"
                [editable]="false"
                [resultTemplate]="rt"
                [inputFormatter]="wdJobService.typeaheadSearchJobsFormatter"
                [required]="true"
                [disabled]="isSaving"
                [ngModelOptions]="{standalone: true}"
                [(ngModel)]="lookahead.job"
                [ngbTypeahead]="wdJobService.typeaheadSearchJobs"
                (blur)="typeaheadJobBlur(lookahead.job)"
                (ngModelChange)="jobChange($event)"
                #jobControl="ngModel"
              />

              <!--Typeahead Result Template-->
              <ng-template #rt let-r="result" let-t="term">
                <ngb-highlight
                  [result]="r.number + ' ' + r.name"
                  [term]="t"
                ></ngb-highlight>
              </ng-template>
            </bc-atoms-input-group>
          </div>
          <div class="col-xs-12 col-sm-4">
            <!--Job Phase-->
            <bc-atoms-input-group
              labelIcon="jobsAdmin"
              labelTitle="{{'order.job.phase.title'|translate}}"
              labelDescription="{{'order.job.phase.description'|translate}}"
              [required]="phaseTypeaheadResults.value.length > 0"
              [state]="jobPhaseControl.status"
              [stateInput]="jobPhaseControl.errors"
              [stateTrigger]="jobPhaseControl.invalid && (jobPhaseControl.touched && jobPhaseControl.dirty)"
            >
              <bc-atoms-typeahead
                id="jobPhase"
                name="jobPhase"
                title="{{'order.job.phase.description'|translate}}"
                placeholder="{{'order.job.phase.title'|translate}}"
                labelKey="label"
                size="small"
                [required]="phaseTypeaheadResults.value.length > 0"
                [disabled]="isSaving || phaseTypeaheadResults.value.length === 0"
                [options]="phaseTypeaheadResults|async"
                [(model)]="lookahead.phase"
                [(ngModel)]="lookahead.phase"
                #jobPhaseControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-typeahead>
            </bc-atoms-input-group>
          </div>
          <div class="col-xs-12 col-sm-4">
            <!--Ordered For-->
            <bc-atoms-input-group
              labelIcon="user"
              labelTitle="{{'order.details.user.orderedFor.title'|translate}}"
              labelDescription="{{'order.details.user.orderedFor.description'|translate}}"
              [required]="true"
              [state]="orderedForControl.status"
              [stateInput]="orderedForControl.errors"
              [stateTrigger]="orderedForControl.invalid && (orderedForControl.touched && orderedForControl.dirty)"
            >
              <bc-atoms-typeahead
                id="orderedFor"
                name="orderedFor"
                title="{{'order.details.user.orderedFor.description'|translate}}"
                placeholder="{{'order.details.user.orderedFor.title'|translate}}"
                labelKey="fullName"
                size="small"
                [required]="wdEnvironmentService.permissions.canSubmitOrdersOnBehalf"
                [disabled]="isSaving || wdManageOrdersService.orderDetailsData.users.value.length === 0 || !wdEnvironmentService.permissions.canSubmitOrdersOnBehalf"
                [options]="wdManageOrdersService.orderDetailsData.users|async"
                [(model)]="lookahead.orderedFor"
                [(ngModel)]="lookahead.orderedFor"
                #orderedForControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-typeahead>
            </bc-atoms-input-group>
          </div>
          <div class="col-xs-12 col-sm-4">
            <!--Measured By-->
            <bc-atoms-input-group
              labelIcon="user"
              labelTitle="{{'order.details.user.measuredBy.title'|translate}}"
              labelDescription="{{'order.details.user.measuredBy.description'|translate}}"
              [required]="false"
              [state]="measuredByControl.status"
              [stateInput]="measuredByControl.errors"
              [stateTrigger]="measuredByControl.invalid && (measuredByControl.touched && measuredByControl.dirty)"
            >
              <bc-atoms-input
                id="measuredBy"
                name="measuredBy"
                title="{{'order.details.user.measuredBy.description'|translate}}"
                placeholder="{{'order.details.user.measuredBy.title'|translate}}"
                size="small"
                [required]="false"
                [disabled]="isSaving"
                [(model)]="lookahead.measuredBy"
                [(ngModel)]="lookahead.measuredBy"
                #measuredByControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-input>
            </bc-atoms-input-group>
          </div>
          <div class="col-xs-12 col-sm-4">
            <!--User Group-->
            <bc-atoms-input-group
              labelIcon="userGroup"
              labelTitle="{{'order.details.user.group.title'|translate}}"
              labelDescription="{{'order.details.user.group.description'|translate}}"
              [required]="true"
              [state]="userGroupControl.status"
              [stateInput]="userGroupControl.errors"
              [stateTrigger]="userGroupControl.invalid && (userGroupControl.touched && userGroupControl.dirty)"
            >
              <bc-atoms-typeahead
                id="userGroup"
                name="userGroup"
                title="{{'order.details.user.group.description'|translate}}"
                placeholder="{{'order.details.user.group.title'|translate}}"
                labelKey="label"
                size="small"
                [required]="true"
                [disabled]="isSaving || wdManageOrdersService.orderDetailsData.userGroups.value.length === 0"
                [options]="wdManageOrdersService.orderDetailsData.userGroups|async"
                [(model)]="lookahead.userGroup"
                [(ngModel)]="lookahead.userGroup"
                #userGroupControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-typeahead>
            </bc-atoms-input-group>
          </div>
          <div class="col-xs-12 col-sm-4">
            <!--PO Number-->
            <bc-atoms-input-group
              labelIcon="workOrders"
              labelTitle="{{'order.details.poNumber.title'|translate}}"
              labelDescription="{{'order.details.poNumber.description'|translate}}"
              [required]="false"
              [state]="poControl.status"
              [stateInput]="poControl.errors"
              [stateTrigger]="poControl.invalid && (poControl.touched && poControl.dirty)"
            >
              <bc-atoms-input
                id="poNumber"
                name="poNumber"
                title="{{'order.details.poNumber.description'|translate}}"
                placeholder="{{'order.details.poNumber.title'|translate}}"
                size="small"
                [required]="false"
                [disabled]="isSaving"
                [(model)]="lookahead.poNumber"
                [(ngModel)]="lookahead.poNumber"
                #poControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-input>
            </bc-atoms-input-group>
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12">
            <hr />
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12 col-sm-3">
            <!--Ordered-->
            <bc-atoms-input-group
              labelIcon="calendar"
              labelTitle="{{'order.dates.ordered.title'|translate}}"
              labelDescription="{{'order.dates.ordered.description'|translate}}"
              [required]="true"
            >
              <bc-atoms-date-picker
                id="dateOrdered"
                name="dateOrdered"
                placeholder="{{'order.dates.ordered.title'|translate}}"
                title="{{'order.dates.ordered.description'|translate}}"
                size="small"
                [disabled]="true"
                [dateFormat]="wdEnvironmentService.dateFormat"
                [model]="lookahead.dateOrdered"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-date-picker>
            </bc-atoms-input-group>
          </div>
          <div class="col-xs-12 col-sm-3">
            <!--Requested-->
            <bc-atoms-input-group
              labelIcon="calendar"
              labelTitle="{{'order.dates.requested.title'|translate}}"
              labelDescription="{{'order.dates.requested.description'|translate}}"
              [required]="true"
            >
              <bc-atoms-date-picker
                id="dateRequested"
                name="dateRequested"
                placeholder="{{'order.dates.requested.title'|translate}}"
                title="{{'order.dates.requested.description'|translate}}"
                size="small"
                [min]="minDateRequested"
                [disabled]="false"
                [dateFormat]="wdEnvironmentService.dateFormat"
                [model]="lookahead.dateRequested"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-date-picker>
            </bc-atoms-input-group>
          </div>
          <div class="col-xs-12 col-sm-3">
            <!--Production-->
            <bc-atoms-input-group
              labelIcon="calendar"
              labelTitle="{{'order.dates.production.title'|translate}}"
              labelDescription="{{'order.dates.production.description'|translate}}"
              [required]="true"
            >
              <bc-atoms-date-picker
                id="dateProduction"
                name="dateProduction"
                placeholder="{{'order.dates.production.title'|translate}}"
                title="{{'order.dates.production.description'|translate}}"
                size="small"
                [min]="lookahead?.dateOrdered"
                [max]="lookahead?.dateShipping"
                [required]="true"
                [disabled]="isSaving"
                [dateFormat]="wdEnvironmentService.dateFormat"
                [(model)]="lookahead.dateProduction"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-date-picker>
            </bc-atoms-input-group>
          </div>
          <div class="col-xs-12 col-sm-3">
            <!--Shipping-->
            <bc-atoms-input-group
              labelIcon="calendar"
              labelTitle="{{'order.dates.shipping.title'|translate}}"
              labelDescription="{{'order.dates.shipping.description'|translate}}"
              [required]="true"
            >
              <bc-atoms-date-picker
                id="dateShipping"
                name="dateShipping"
                placeholder="{{'order.dates.shipping.title'|translate}}"
                title="{{'order.dates.shipping.description'|translate}}"
                size="small"
                [min]="lookahead?.dateProduction"
                [required]="true"
                [disabled]="isSaving"
                [dateFormat]="wdEnvironmentService.dateFormat"
                [(model)]="lookahead.dateShipping"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-date-picker>
            </bc-atoms-input-group>
          </div>
        </div>
      </form>
    </div>
    <!--Dialog Actions-->
    <div footer-content>
      <div class="buttons-container">
        <div class="right-buttons">
          <!--Cancel/Close -->
          <bc-atoms-button
            [label]="('actions.cancel'|translate)"
            [title]="('actions.cancel'|translate)"
            [disabled]="isLoading"
            type="default"
            icon="cancel"
            display="block"
            size="small"
            (onButtonClicked)="onDismiss()"
          >
          </bc-atoms-button>
          <!--Save -->
          <bc-atoms-button
            [label]="('actions.save'|translate)"
            [title]="('actions.save'|translate)"
            [disabled]="isLoading || !isChanges || !isDatesValid || !lookaheadsForm.form.valid"
            type="success"
            icon="save"
            display="block"
            size="small"
            (onButtonClicked)="onSubmit(lookahead)"
          >
          </bc-atoms-button>
        </div>
      </div>
    </div>
  </bc-templates-modal-layout>
</div>
